import { useCallback, useEffect, useMemo, useState } from 'react';
import { useThemeWatcher } from './hooks/useThemeWatcher';
import './index.css';
import { useAppStore } from './store/useAppStore';
import type { DetailSource } from './store/useAppStore';
import { useProjectsStore } from './store/useProjectsStore';
import { PublicProjectScreen } from './components/PublicProjectScreen';
import { AppLayout } from './components/AppLayout';
import { ShowcaseScreen } from './screens/ShowcaseScreen';
import { MyProjectsScreen } from './screens/my-projects/MyProjectsScreen';
import { RequestsScreen } from './screens/RequestsScreen';
import { ProjectDetailScreenContainer } from './screens/ProjectDetailScreen';
import { WizardScreen } from './screens/WizardScreen';
import { installGlobalErrorTracking } from './lib/fetcher';
import { installGlobalTapTracking } from './lib/tapTracker';
import { isSelfTestEnabled } from './lib/selfTest';
import { Onboarding, shouldShowOnboarding } from './components/Onboarding';
import { getTelegramStartParam, processReferralOnStart } from './utils/referral';
import { getProjects, createDraft } from './lib/api';
import { ProfileScreen } from './components/ProfileScreen';
import { UserProfileScreen } from './screens/UserProfileScreen';
import { SettingsScreen } from './components/SettingsScreen';
import { useStreak } from './hooks/useStreak';
import { useBadges } from './hooks/useBadges';
import { EmojiStatusOffer } from './components/EmojiStatusOffer';

type RouteState = { kind: 'app' } | { kind: 'public'; idOrSlug: string };

function parseRoute(pathname: string): RouteState {
  const parts = String(pathname || '/').split('/').filter((p) => p.length > 0);
  if (parts[0] !== 'p' || parts.length < 2) return { kind: 'app' };
  try {
    const idOrSlug = decodeURIComponent(parts[1] || '').trim();
    return idOrSlug ? { kind: 'public', idOrSlug } : { kind: 'app' };
  } catch {
    return { kind: 'app' };
  }
}

function App() {
  useThemeWatcher();
  const [selfTestVisible, setSelfTestVisible] = useState(useMemo(() => isSelfTestEnabled(), []));
  const [route, setRoute] = useState<RouteState>(() => (typeof window !== 'undefined' ? parseRoute(window.location.pathname) : { kind: 'app' }));
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showEmojiOffer, setShowEmojiOffer] = useState(false);
  const { screen, selectedProjectId, activeTab } = useAppStore();

  // Phase 4: daily streak + badge check on app load
  const { isNewDay } = useStreak();
  const { checkForNewBadges } = useBadges();
  useEffect(() => {
    if (isNewDay) checkForNewBadges();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewDay]);

  useEffect(() => {
    installGlobalErrorTracking();
    installGlobalTapTracking();
    shouldShowOnboarding().then((show) => { if (show) setShowOnboarding(true); });
    processReferralOnStart();
  }, []);

  useEffect(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (ua.includes('performanceClass=low')) {
      document.documentElement.classList.add('reduce-motion');
    }
  }, []);
  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    useAppStore.getState().setCreateProject(async () => {
      try {
        const draft = await createDraft();
        useAppStore.getState().setSelectedProjectId(draft.id);
        useAppStore.getState().setScreen('wizard');
        useAppStore.getState().setActiveTab('my');
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Не удалось создать проект';
        useAppStore.getState().addNotification(msg);
      }
    });
    return () => useAppStore.getState().setCreateProject(null);
  }, []);

  // Handle Telegram startapp deep-link: project_<id> or p_<id> (Stories), ref_<id> (referral)
  useEffect(() => {
    try {
      const startParam = getTelegramStartParam();
      if (!startParam) return;

      if (startParam.startsWith('p_') || startParam.startsWith('project_')) {
        const prefix = startParam.startsWith('p_') ? 'p_' : 'project_';
        const projectId = startParam.slice(prefix.length);
        if (projectId) {
          openDetail(projectId, 'public');
        }
        return;
      }

      if (startParam === 'support_stars') {
        useAppStore.getState().setScreen('settings');
      }
      // ref_ is handled by processReferralOnStart()
    } catch {
      // ignore
    }
  // run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = useCallback((id: string, source: DetailSource = 'public') => {
    useAppStore.getState().setScreen('detail');
    useAppStore.getState().setSelectedProjectId(id);
    useAppStore.getState().setDetailSource(source);
  }, []);

  if (showOnboarding) {
    return <Onboarding onComplete={() => setShowOnboarding(false)} />;
  }

  if (route.kind === 'public') {
    return (
      <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)}>
        <PublicProjectScreen idOrSlug={route.idOrSlug} onBack={() => window.history.back()} />
      </AppLayout>
    );
  }

  if (screen === 'detail') {
    return (
      <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)} onOpenProject={openDetail}>
        <ProjectDetailScreenContainer />
      </AppLayout>
    );
  }

  if (screen === 'profile') {
    return (
      <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)}>
        {showEmojiOffer && <EmojiStatusOffer onDismiss={() => setShowEmojiOffer(false)} />}
        <ProfileScreen onBack={() => useAppStore.getState().setScreen('list')} />
      </AppLayout>
    );
  }

  if (screen === 'settings') {
    return (
      <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)}>
        <SettingsScreen onBack={() => useAppStore.getState().setScreen('profile')} />
      </AppLayout>
    );
  }

  if (screen === 'user-profile') {
    const viewUserId = useAppStore.getState().viewProfileUserId ?? useAppStore.getState().viewProfileTelegramId;
    if (viewUserId) {
      return (
        <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)}>
          <UserProfileScreen
            userId={viewUserId}
            onBack={() => useAppStore.getState().setScreen('list')}
          />
        </AppLayout>
      );
    }
  }

  if (screen === 'wizard' && selectedProjectId) {
    return (
      <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)}>
        <WizardScreen
          projectId={selectedProjectId}
          onBack={() => { useAppStore.getState().setScreen('list'); useAppStore.getState().setSelectedProjectId(null); }}
          onOpenDetails={openDetail}
          onAfterSave={() => {
            const id = useAppStore.getState().selectedProjectId;
            getProjects().then((data) => {
              useProjectsStore.getState().setProjects(data);
              const p = data.find((x) => x.id === id);
              if (p?.status === 'submitted') {
                useAppStore.getState().addNotification('Проект отправлен на модерацию');
              }
            });
          }}
        />
      </AppLayout>
    );
  }

  const MainContent =
    activeTab === 'my' ? MyProjectsScreen : activeTab === 'requests' ? RequestsScreen : ShowcaseScreen;

  return (
    <AppLayout showSelfTest={selfTestVisible} onSelfTestClose={() => setSelfTestVisible(false)} onOpenProject={openDetail}>
      <MainContent />
    </AppLayout>
  );
}

export default App;
